import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Calendar, Clock, User, Phone, Mail, MapPin, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { todayISO } from '../services/appointmentService';

const PageContainer = styled.div`
  padding-top: calc(var(--header-h) + 40px);
  padding-bottom: 80px;
  min-height: 100vh;
  background: white;
  width: 100%;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 30px;
  margin-left: 0;
  transition: color 0.3s ease;

  &:hover {
    color: #666;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 50px;

  h1 {
    font-size: 2.5rem;
    color: #333;
    margin-bottom: 15px;
    font-weight: 300;
  }

  p {
    font-size: 1.1rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const AppointmentForm = styled.form`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Input = styled.input`
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #333;
  }

  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #333;
  }

  option:disabled {
    text-decoration: line-through;
    color: #999;
    background: #f0f0f0;
  }
`;

const TextArea = styled.textarea`
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  resize: vertical;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: #333;
  }

  &::placeholder {
    color: #999;
  }
`;

const FullWidthGroup = styled.div`
  grid-column: 1 / -1;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 18px 30px;
  background: #333;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background 0.3s ease;
  margin-top: 20px;

  &:hover {
    background: #555;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const InfoSection = styled.div`
  margin-top: 40px;
  padding: 30px;
  background: #f8f4f0;
  border-radius: 15px;
  border-left: 4px solid #333;

  h3 {
    font-size: 1.3rem;
    color: #333;
    margin-bottom: 15px;
    font-weight: 600;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: 10px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 10px;

    &::before {
      content: '✓';
      color: #333;
      font-weight: bold;
    }
  }
`;

const AppointmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    appointmentDate: '',
    appointmentTime: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disabledTimeSlots, setDisabledTimeSlots] = useState<Set<string>>(new Set());

  // Check available time slots when date is selected
  useEffect(() => {
    if (formData.appointmentDate) {
      checkAvailableTimeSlots(formData.appointmentDate);
    }
  }, [formData.appointmentDate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      // If date changes, clear selected time
      if (name === 'appointmentDate') {
        return {
          ...prev,
          appointmentDate: value,
          appointmentTime: ''
        };
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const checkAvailableTimeSlots = async (date: string) => {
    if (!date) {
      setDisabledTimeSlots(new Set());
      return;
    }

    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');

      // Dolu saatler: PII icermeyen 'bookedSlots' koleksiyonundan okunur.
      // 'appointments' koleksiyonunda musteri iletisim bilgileri var ve artik
      // disariya kapali; saat musaitligi icin oraya bakmaya gerek yok.
      const bookedSlotsRef = collection(db, 'bookedSlots');
      const q = query(bookedSlotsRef, where('date', '==', date));

      const querySnapshot = await getDocs(q);
      const bookedSlots = new Set<string>();

      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.time) {
          bookedSlots.add(data.time);
        }
      });

      // Check disabled time slots
      const disabledRef = collection(db, 'disabledTimeSlots');
      const disabledQuery = query(
        disabledRef,
        where('date', '==', date),
        where('active', '==', true)
      );
      
      const disabledSnapshot = await getDocs(disabledQuery);
      disabledSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.time) {
          bookedSlots.add(data.time);
        }
      });

      setDisabledTimeSlots(bookedSlots);
    } catch (error) {
      console.error('Error checking available time slots:', error);
    }
  };

  // Bugunden onceki tarihler secilemez. Tarayici min ile engeller,
  // gonderimde de ayrica kontrol edilir (min istemci tarafinda asilabilir).
  const minAppointmentDate = todayISO();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Import the appointment services
      const { 
        sendAppointmentEmail, 
        saveToGoogleSheets, 
        saveAppointmentToFirestore 
      } = await import('../services/appointmentService');
      
      // Send email, save to Google Sheets, and save to Firestore
      const [emailSent, sheetSaved, firestoreSaved] = await Promise.all([
        sendAppointmentEmail(formData),
        saveToGoogleSheets(formData),
        saveAppointmentToFirestore(formData)
      ]);
      
      if (firestoreSaved) {
        const successMsg = emailSent && sheetSaved
          ? 'Randevu talebiniz başarıyla alındı! Email gönderildi ve Google Sheets\'e kaydedildi.'
          : 'Randevu talebiniz başarıyla alındı! En kısa sürede sizinle iletişime geçeceğiz.';
        
        alert(successMsg);
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          appointmentDate: '',
          appointmentTime: '',
          serviceType: '',
          message: ''
        });
      } else {
        alert('Randevu talebi kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (error) {
      console.error('Error submitting appointment:', error);
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Geri Dön
        </BackButton>

        <Header>
          <h1>Randevu Al</h1>
          <p>
            Hayalinizdeki gelinliği bulmak için randevu alın. Showroomumuzu ziyaret edin 
            ve uzman ekibimizden profesyonel danışmanlık alın.
          </p>
        </Header>

        <AppointmentForm onSubmit={handleSubmit}>
          <FormGrid>
            <FormGroup>
              <Label>
                <User size={16} />
                Ad
              </Label>
              <Input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Adınızı girin"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <User size={16} />
                Soyad
              </Label>
              <Input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Soyadınızı girin"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Mail size={16} />
                E-posta
              </Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="ornek@email.com"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Phone size={16} />
                Telefon
              </Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+90 5XX XXX XX XX"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Calendar size={16} />
                Randevu Tarihi
              </Label>
              <Input
                type="date"
                name="appointmentDate"
                value={formData.appointmentDate}
                onChange={handleInputChange}
                required
                /* Gecmis tarihe randevu alinamaz */
                min={minAppointmentDate}
                aria-label="Randevu tarihi seçiniz"
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Clock size={16} />
                Randevu Saati
              </Label>
              <Select
                name="appointmentTime"
                value={formData.appointmentTime}
                onChange={handleInputChange}
                required
                aria-label="Randevu saati seçiniz"
                title="Randevu saati seçiniz"
              >
                <option value="">Saat seçiniz</option>
                {[
                  '10:00', '11:00', '12:00', '13:00', '14:00', 
                  '15:00', '16:00', '17:00', '18:00'
                ].map(time => {
                  const isDisabled = disabledTimeSlots.has(time);
                  return (
                    <option 
                      key={time} 
                      value={time}
                      disabled={isDisabled}
                    >
                      {time} {isDisabled ? '(Dolu)' : ''}
                    </option>
                  );
                })}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>
                <MapPin size={16} />
                Hizmet Türü
              </Label>
              <Select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleInputChange}
                required
                aria-label="Hizmet türü seçiniz"
                title="Hizmet türü seçiniz"
              >
                <option value="">Hizmet seçiniz</option>
                <option value="gelinlik-deneme">Gelinlik Deneme</option>
                <option value="abiye-deneme">Abiye Deneme</option>
                <option value="kinalik-deneme">Kınalık Deneme</option>
                <option value="aksesuar-danismanlik">Aksesuar Danışmanlığı</option>
                <option value="ozel-tasarim">Özel Tasarım Görüşmesi</option>
                <option value="genel-danismanlik">Genel Danışmanlık</option>
              </Select>
            </FormGroup>
          </FormGrid>

          <FullWidthGroup>
            <FormGroup>
              <Label>
                <User size={16} />
                Mesajınız
              </Label>
              <TextArea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Özel isteklerinizi veya sorularınızı buraya yazabilirsiniz..."
              />
            </FormGroup>
          </FullWidthGroup>

          <SubmitButton type="submit" disabled={isSubmitting}>
            <Calendar size={20} />
            {isSubmitting ? 'Gönderiliyor...' : 'Randevu Talebi Gönder'}
          </SubmitButton>
        </AppointmentForm>

        <InfoSection>
          <h3>Randevu Bilgileri</h3>
          <ul>
            <li>Randevular Pazartesi-Cumartesi 10:00-19:00 arası alınmaktadır</li>
            <li>Randevu talebiniz 24 saat içinde onaylanacaktır</li>
            <li>Randevu saatinden 15 dakika önce showroomumuzda olmanızı rica ederiz</li>
            <li>Randevu iptali için en az 24 saat önceden haber vermeniz gerekmektedir</li>
            <li>Showroomumuzda ücretsiz park imkanı bulunmaktadır</li>
          </ul>
        </InfoSection>
      </Container>
    </PageContainer>
  );
};

export default AppointmentPage;
